import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  PlatformAssistantConfiguration,
  RevisionedPlatformAssistantConfiguration,
} from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { useSavePlatformAssistantConfigurationMutation } from '@/hooks/useAssistantConfiguration';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { AccessAreaList } from './AccessAreaList';
import { BehaviourFields } from './BehaviourFields';
import { ConfigurationCard } from './ConfigurationCard';
import { InstructionsField } from './InstructionsField';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { SaveBar } from './SaveBar';
import { toggleAccessArea } from './assistantConfigurationOptions';
import { normaliseInstructions } from './normaliseInstructions';
import { useConfigurationSaveNotifications } from './useConfigurationSaveNotifications';
import { useSyncedDraft } from './useSyncedDraft';

const UPDATE_PERMISSION = 'assistant_configuration.update';

interface PlatformAssistantConfigurationFormProps {
  saved: RevisionedPlatformAssistantConfiguration;
}

export function PlatformAssistantConfigurationForm({
  saved,
}: PlatformAssistantConfigurationFormProps) {
  const { t } = useTranslation('assistant');
  const canUpdate = useHasPlatformPermission(UPDATE_PERMISSION);
  const saveMutation = useSavePlatformAssistantConfigurationMutation();
  const notifications = useConfigurationSaveNotifications();
  const configuration = saved.configuration;
  const { draft, setDraft, baseRevision, adoptSaved } = useSyncedDraft(
    configuration,
    saved.revision
  );
  const instructions = draft.extraInstructions === null ? '' : draft.extraInstructions;
  const nextConfiguration: PlatformAssistantConfiguration = {
    ...draft,
    extraInstructions: normaliseInstructions(instructions),
  };
  const hasChanges = JSON.stringify(nextConfiguration) !== JSON.stringify(configuration);

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    saveMutation.mutate(
      { value: nextConfiguration, expectedRevision: baseRevision },
      {
        onSuccess: function adoptAndNotify(savedConfiguration) {
          adoptSaved(savedConfiguration.configuration, savedConfiguration.revision);
          notifications.onSuccess();
        },
        onError: notifications.onError,
      }
    );
  }

  return (
    <form onSubmit={submit}>
      <fieldset disabled={canUpdate === false} className="space-y-5">
        {canUpdate === false ? <ReadOnlyNotice /> : null}
        <ConfigurationCard title={t('configuration.behaviourTitle')}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BehaviourFields
              tone={draft.tone}
              replyLength={draft.replyLength}
              inheritedTone={null}
              inheritedReplyLength={null}
              onToneChange={function updateTone(nextTone) {
                if (nextTone !== null) {
                  setDraft({ ...draft, tone: nextTone });
                }
              }}
              onReplyLengthChange={function updateReplyLength(nextReplyLength) {
                if (nextReplyLength !== null) {
                  setDraft({ ...draft, replyLength: nextReplyLength });
                }
              }}
            />
            <InstructionsField
              fieldId="platform-assistant-instructions"
              value={instructions}
              hint={t('configuration.instructions.platformHint')}
              onChange={function updateInstructions(nextInstructions) {
                setDraft({ ...draft, extraInstructions: nextInstructions });
              }}
            />
          </div>
        </ConfigurationCard>

        <ConfigurationCard
          title={t('configuration.areas.title')}
          hint={t('configuration.areas.hint')}
        >
          <AccessAreaList
            resolveRowState={function resolveRowState(area) {
              return {
                isOn: draft.allowedAccessAreas.includes(area),
                isLocked: false,
                note: null,
                noteActionLabel: null,
              };
            }}
            onToggle={function toggleArea(area) {
              setDraft({
                ...draft,
                allowedAccessAreas: toggleAccessArea(draft.allowedAccessAreas, area),
              });
            }}
          />
        </ConfigurationCard>

        <ConfigurationCard title={t('configuration.delegation.title')}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-zooindigo">
                {t('configuration.delegation.platformLabel')}
              </p>
              <p className="text-xs text-zoomuted mt-0.5">
                {t('configuration.delegation.platformHint')}
              </p>
            </div>
            <ToggleSwitch
              isChecked={draft.ownersCanCustomize}
              accessibleLabel={t('configuration.delegation.platformLabel')}
              onToggle={function toggleDelegation() {
                setDraft({ ...draft, ownersCanCustomize: !draft.ownersCanCustomize });
              }}
            />
          </div>
        </ConfigurationCard>

        <ConfigurationCard title={t('configuration.proactive.title')}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-zooindigo">
                {t('configuration.proactive.label')}
              </p>
              <p className="text-xs text-zoomuted mt-0.5">{t('configuration.proactive.hint')}</p>
            </div>
            <ToggleSwitch
              isChecked={draft.proactiveFeaturesEnabled}
              accessibleLabel={t('configuration.proactive.label')}
              onToggle={function toggleProactive() {
                setDraft({ ...draft, proactiveFeaturesEnabled: !draft.proactiveFeaturesEnabled });
              }}
            />
          </div>
        </ConfigurationCard>

        {canUpdate ? <SaveBar hasChanges={hasChanges} isSaving={saveMutation.isPending} /> : null}
      </fieldset>
    </form>
  );
}
